import {
  type AreaComponent,
  type FaceDefinition,
  flattenPolygonRing,
  MIN_OUTLINE_POINTS,
  MIN_SHAPE_POINTS,
  type Placement,
  type PlacementPosition,
  type ResolvedFacePhoto,
  type ShapeVertex,
  type SizeMm,
  type Surface,
  type SurfacePoint,
} from '@repo/api-contracts';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { HoleDrawTool } from '@/entities/edit-area';
import {
  componentGrid,
  type EditableSurface,
  editableVerticesOf,
  normalizeEditedShape,
  type Rect,
  scalePolygonShape,
  surfaceMmToPhoto,
  surfaceScale,
  translateHoleRing,
  translateVertices,
} from '@/entities/edit-area';
import { clampToSurface } from '../model/clamp-to-surface';
import type { CylinderPreviewOverlay } from '../model/use-cylinder-preview';
import { useHoleMovePointer } from '../model/use-hole-move-pointer';
import { useOutlinePointer } from '../model/use-outline-pointer';
import { usePhotoPxPerScreenPx } from '../model/use-photo-px-per-screen-px';
import { usePlacementPointer } from '../model/use-placement-pointer';
import { usePointEditPointer } from '../model/use-point-edit-pointer';
import { type DrawAreaKind, useRectDrawPointer } from '../model/use-rect-draw-pointer';
import { useResizePointer } from '../model/use-resize-pointer';
import { useShapePointer } from '../model/use-shape-pointer';
import { CylinderPreviewLayer } from './cylinder-preview-layer';
import { CylinderSurfaceOverlay } from './cylinder-surface-overlay';
import { DrawPreviewOverlay } from './draw-preview-overlay';
import { type DrawTool, DrawToolBar } from './draw-tool-bar';
import { FlatSurfaceOverlay } from './flat-surface-overlay';
import { KeyHints } from './key-hints';
import { MmSizeLabel } from './mm-size-label';
import { OutlinePathOverlay } from './outline-path-overlay';
import { PlacementOverlay } from './placement-overlay';
import { PointEditOverlay } from './point-edit-overlay';
import { ProductExtractButton } from './product-extract-button';
import { ResizeHandlesOverlay } from './resize-handles-overlay';
import { ShapeDrawingBlockedNotice } from './shape-drawing-blocked-notice';
import { ShapeHoleOverlay } from './shape-hole-overlay';
import { SurfaceOutlineOverlay } from './surface-outline-overlay';
import { ZoomControl } from './zoom-control';

interface Props {
  photo: ResolvedFacePhoto | undefined;
  components: readonly AreaComponent[];
  faceDefinition: FaceDefinition;
  /** 축척 안내에 쓰는 면의 표면. 표면은 사진 위에서 고르거나 고치지 않는다(다시 추출하기·표면 삭제로만 바꾼다). */
  selectedSurfaceId: string | null;
  /** 상품 실측. null 이면 추출할 수 없다 — 표면 실측을 채울 값이 없다(원통 표면이 있으면 윤곽 교체만 된다). */
  measurement: SizeMm | null;
  /** 운영자가 닫은 윤곽(사진 %). 표면 생성·교체는 호출자가 한다. */
  onSurfaceExtract: (points: SurfacePoint[]) => void;
  onPlacementMove: (componentId: string, position: Placement['position']) => void;
  disabled: boolean;
  /** 저장하지 않는 원통 미리보기. 사진이 보일 때만 사진 바로 위에 겹친다. */
  cylinderPreview?: CylinderPreviewOverlay | null;
  /** 점을 찍어 그리는 중인 인쇄 영역. null(기본)이면 그리지 않는다. */
  shapeDrawing?: {
    readonly name: string;
    readonly mode: 'outer' | 'hole';
    readonly holeTool?: HoleDrawTool;
  } | null;
  /** 닫은 인쇄 영역 점열(표면 mm). 거부 사유를 돌려주면 점을 남기고 사유를 보인다. */
  onShapeClose?: (points: PlacementPosition[]) => string | null;
  /** Esc 로 그리기를 그만둘 때. */
  onShapeCancel?: () => void;
  /** 도구 줄의 자유 다각형 — 자동 이름으로 점 찍기 그리기를 시작한다. 없으면 버튼이 꺼진다. */
  onBeginPolygon?: () => void;
  /** 사각형·원형 도구로 끌어 놓은 사각형(표면 mm). 만들면 null, 거부되면 사유. 없으면 두 도구가 꺼진다. */
  onDrawArea?: (kind: DrawAreaKind, rect: Rect) => string | null;
  /**
   * 원통 표면에서 사각형·원형 도구를 누를 때 — 끌어 그리는 대신 표면 가운데에 기본 크기로 바로 만든다.
   * 만들면 null, 거부되면 사유. 없으면 원통에서 두 도구가 꺼진다.
   */
  onCreateDefaultArea?: (kind: DrawAreaKind) => string | null;
  /**
   * 인쇄 제외 영역을 사각형·원형 도구로 그리는 중(shapeDrawing.mode 'hole')에 끌어 놓은 사각형(표면 mm).
   * 더하면 null, 거부되면 사유. 없으면 끌어 그리지 않는다.
   */
  onDrawHole?: (kind: DrawAreaKind, rect: Rect) => string | null;
  /** 고른 영역의 고른 구멍 순번(selection.holeIndex). */
  selectedHoleIndex?: number | null;
  /** 사진 위에서 구멍을 누르거나(순번) Esc 로 풀 때(null). 없으면 사진 위에서 구멍을 고르지 않는다. */
  onSelectHole?: (index: number | null) => void;
  /** 끌거나 화살표 키로 옮긴 구멍. 옮기면 null, 거부되면 사유(구멍은 그대로). */
  onMoveHole?: (componentId: string, index: number, dxMm: number, dyMm: number) => string | null;
  /** Delete 키로 지운 구멍. 지우면 null, 거부되면 사유. */
  onDeleteHole?: (componentId: string, index: number) => string | null;
  /**
   * 적용하지 않은 입력 때문에 잠겼는가. 그러면 작업 화면이 입력 안내(입력 취소·그리기 취소)를 하나로 보이므로
   * 그리기 쪽 막힘 안내는 숨긴다.
   */
  inputsPending?: boolean;
  /** 도구 줄의 전체 영역. 없으면 버튼이 꺼진다. */
  fullArea?: FullAreaControl;
  /** 사진 위에서 고른 인쇄 영역(selection.componentId). */
  selectedComponentId?: string | null;
  /**
   * 선택 도구로 영역을 누르거나(id) 빈 곳·Esc(null)로 선택을 바꿀 때. 바꿨으면 true, 확인 창에서 그만두면 false.
   * 없으면 사진 위 선택을 하지 않는다(누른 영역은 지금처럼 옮기기만 한다).
   */
  onSelectComponent?: (componentId: string | null) => boolean;
  /** 핸들로 끌어 바꾼 크기(표면 mm). 바꾸면 null, 거부되면 사유. 없으면 핸들을 보이지 않는다. */
  onPlacementResize?: (componentId: string, rect: Rect) => string | null;
  /**
   * 화살표 키로 옮긴 고른 영역의 위치(표면 규칙 안으로 자른 값). 옮기면 null, 거부되면 사유(위치는 그대로).
   * 없으면 화살표 키로 옮기지 않는다.
   */
  onPlacementNudge?: (componentId: string, position: PlacementPosition) => string | null;
  /** 지금 켜진 도형 도구(선택·사각형·원형). 작업 화면이 쥐어 사이드바 도구 줄과 함께 쓴다. 없으면 캔버스가 쥔다. */
  drawTool?: DrawTool;
  onDrawToolChange?: (tool: DrawTool) => void;
  /**
   * 도구 줄을 그릴 사이드바 칸. 넘기면 그 칸에 그리고(아직 칸이 없으면 null — 그리지 않는다), 넘기지 않으면 사진 위에 둔다.
   */
  toolBarContainer?: HTMLElement | null;
  /**
   * '상품 추출하기' 버튼·추출 막힘 사유·추출 중 안내를 그릴 사이드바 칸(표면 설정). 도구 줄 칸과 같은 규칙이다 — 넘기면 그 칸에
   * 그리고(아직 칸이 없으면 null — 그리지 않는다), 넘기지 않으면 사진 위 줄에 둔다. 추출 상태는 캔버스가 쥔다.
   */
  extractContainer?: HTMLElement | null;
  /** 고른 영역을 이 면에서 지운다(Delete 키). 지우면 null, 거부되면 사유. 없으면 Delete 키로 지우지 않는다. */
  onDeletePlacement?: (componentId: string) => string | null;
  /**
   * 점 편집으로 고친 바깥 고리 점(표면 mm)을 반영한다. 반영하면 null, 거부되면 사유. 없으면 점 편집을 하지 않는다.
   * 선택 도구로 고른 평면 위 영역을 다시 누르거나 더블클릭하면 점 편집이 켜진다.
   */
  onEditShapePoints?: (componentId: string, points: ShapeVertex[]) => string | null;
  /**
   * 구멍 점 편집으로 고친 구멍 점(표면 mm)을 반영한다. 반영하면 null, 거부되면 사유. 없으면 구멍 점 편집을 하지 않는다.
   * 고른 구멍을 다시 누르거나 더블클릭하면 그 구멍의 점 편집이 켜진다.
   */
  onEditHolePoints?: (componentId: string, index: number, points: ShapeVertex[]) => string | null;
}

/** 도구 줄의 '전체 영역' 버튼 상태(작업 화면이 스토어로 계산한다). */
interface FullAreaControl {
  readonly disabled: boolean;
  readonly reason: string | null;
  readonly error: string | null;
  readonly apply: () => void;
}

const NO_SHAPE_CLOSE = (): string | null => null;
const NO_SHAPE_CANCEL = (): void => undefined;
const CYLINDER_CREATE_TITLE =
  '원통 상품은 표면 가운데에 기본 크기로 만든 뒤 끌어 옮기고 숫자로 크기를 맞춥니다.';
const NO_SURFACE_DRAW_REASON = '표면을 먼저 추출해 주세요.';
const MANY_SURFACES_DRAW_REASON =
  '표면이 1개가 될 때까지 그릴 수 없습니다. 표면 설정에서 나머지 표면을 삭제해 주세요.';

/**
 * 도형 도구가 꺼진 사유. `note` 는 운영자가 고쳐야 풀리는 상태라 도구 줄 아래에 한 줄로 보이고,
 * `title` 은 버튼에 올려 보는 사유다(사진 로드 전·잠금·입력 중처럼 곧 풀리는 것은 title 에만 둔다).
 */
const drawToolReasons = ({
  surfaceCount,
  disabled,
  photoReady,
}: {
  surfaceCount: number;
  disabled: boolean;
  photoReady: boolean;
}): {
  rect: { note: string | null; title: string | null };
  polygon: { note: string | null; title: string | null };
} => {
  const surfaceNote =
    surfaceCount === 0
      ? NO_SURFACE_DRAW_REASON
      : surfaceCount > 1
        ? MANY_SURFACES_DRAW_REASON
        : null;
  const transient = disabled
    ? '적용하지 않은 입력을 먼저 적용하거나 취소한 뒤 그릴 수 있습니다.'
    : !photoReady
      ? '사진을 불러온 뒤 그릴 수 있습니다.'
      : null;
  return {
    rect: { note: surfaceNote, title: surfaceNote ?? transient },
    polygon: { note: surfaceNote, title: surfaceNote ?? transient },
  };
};
const NO_RESIZE = (): string | null => null;
const TYPING_TAGS: readonly string[] = ['INPUT', 'TEXTAREA', 'SELECT'];
/** 화살표 키의 이동 방향(1mm 단위, Shift 는 10배). */
const ARROW_STEPS: Readonly<Record<string, { readonly x: number; readonly y: number }>> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};
/** 사진 확대(%) — 100% 는 사진 전체가 스크롤 없이 보이는 '화면에 맞춤' 크기이고, 다른 값은 그 크기의 배율이다. */
const ZOOM_STEPS = [75, 100, 125, 150, 200, 300, 400] as const;
const DEFAULT_ZOOM_INDEX = 1;
/** 화면에 맞춤에서 사진 칸 위·아래에 남겨 둘 높이(도구 줄·안내·축척 줄과 sticky 여백). */
const FIT_RESERVED_HEIGHT = '14rem';
/** 핸들을 화면에서 잡을 수 있는 최소 한 변(화면 px). */
const MIN_HANDLE_SCREEN_PX = 12;
/** 막 켠 구멍 점 편집을 이 시간(ms) 안에 다시 누르면 더블클릭의 두 번째 누르기로 보고 끄지 않는다. */
const DOUBLE_PRESS_MS = 400;

/** 사진 svg 크기. 맞춤 너비는 칸 너비와 (화면 높이 − 위아래 여백) × 사진 비율 중 작은 쪽이고, 확대율은 그 배율이다. */
const photoSizeStyle = (zoomPercent: number, photoSize: { widthPx: number; heightPx: number }) => {
  const aspect = photoSize.heightPx > 0 ? photoSize.widthPx / photoSize.heightPx : 1;
  const fitWidth = `min(100%, calc((100vh - ${FIT_RESERVED_HEIGHT}) * ${aspect}))`;
  if (zoomPercent === 100) return { width: fitWidth, maxWidth: '100%', height: 'auto' };
  return { width: `calc(${fitWidth} * ${zoomPercent / 100})`, maxWidth: 'none', height: 'auto' };
};

/** 끄는 중인 크기로 바꾼 영역. 자유 다각형은 점과 구멍을 외접 사각형 비율대로 늘인다(미리보기 전용). */
const resizedComponentOf = (component: AreaComponent, rect: Rect): AreaComponent => {
  const size = { widthMm: rect.widthMm, heightMm: rect.heightMm };
  return component.shape?.type === 'polygon'
    ? { ...component, ...size, shape: scalePolygonShape(component.shape, component, size) }
    : { ...component, ...size };
};

const RECT_DRAW_GUIDE = '사진 위를 끌어서 인쇄 영역을 그리세요.';
const HOLE_KEYS = [
  { keyName: '화살표', label: '1mm 이동(Shift 10mm)' },
  { keyName: 'Delete', label: '인쇄 제외 영역 삭제' },
  { keyName: 'Esc', label: '선택 해제' },
] as const;
const POINT_KEYS = [
  { keyName: 'Backspace', label: '마지막 점 삭제' },
  { keyName: 'Esc', label: '취소' },
] as const;
const POINT_EDIT_KEYS = [
  { keyName: 'Esc', label: '끝내기' },
  { keyName: 'Delete', label: '점 삭제' },
  { keyName: '더블클릭', label: '곡선/직선' },
] as const;
const POINT_EDIT_GUIDE =
  '점을 끌어 모양을 고치고, 선 위를 누르면 점을 더합니다. 손잡이를 끌어 곡선을 만들고, Alt 를 누른 채 끌면 손잡이를 따로 꺾습니다.';
const RECT_KEYS = [
  { keyName: 'Shift', label: '정사각형·원' },
  { keyName: 'Esc', label: '취소' },
] as const;

/** 그리기 대상이 있는데 그릴 수 없는 사유. 잠금(적용 안 한 입력·편집 잠금) → 사진 → 표면 순으로 본다. */
const shapeBlockedReason = (
  disabled: boolean,
  photoReady: boolean,
  hasDrawingSurface: boolean,
): string | null => {
  if (disabled) return '적용하지 않은 입력을 먼저 적용하거나 취소한 뒤 그릴 수 있습니다.';
  if (!photoReady) return '사진을 불러온 뒤 그릴 수 있습니다.';
  if (!hasDrawingSurface)
    return '표면이 하나인 면에서만 그릴 수 있습니다. 상품 추출하기로 표면을 먼저 만들어 주세요.';
  return null;
};

/**
 * 추출을 막는 사유. 원통 표면이 이미 있으면 '다시 추출하기'는 윤곽만 바꾸므로(결정 Q2) 상품 실측이 없어도 된다 —
 * 새 표면을 만들 때만 실측으로 표면 실측을 채운다.
 */
const extractBlockedReason = (
  measurement: SizeMm | null,
  surfaces: readonly Surface[],
): string | null => {
  if (measurement === null && surfaces[0]?.type !== 'cylinder')
    return '상품 실측을 등록하면 상품을 추출할 수 있습니다.';
  if (surfaces.length > 1)
    return '표면이 1개가 될 때까지 추출할 수 없습니다. 표면 설정에서 나머지 표면을 삭제해 주세요.';
  return null;
};

/** Parent keys this view by color/face/edit generation to cancel gestures on navigation. */
export function EditAreaCanvas({
  photo,
  components,
  faceDefinition,
  selectedSurfaceId,
  measurement,
  onSurfaceExtract,
  onPlacementMove,
  disabled,
  cylinderPreview = null,
  shapeDrawing = null,
  onShapeClose = NO_SHAPE_CLOSE,
  onShapeCancel = NO_SHAPE_CANCEL,
  onBeginPolygon,
  onDrawArea,
  onCreateDefaultArea,
  onDrawHole,
  selectedHoleIndex = null,
  onSelectHole,
  onMoveHole,
  onDeleteHole,
  inputsPending = false,
  fullArea,
  selectedComponentId = null,
  onSelectComponent,
  onPlacementResize,
  onDeletePlacement,
  onPlacementNudge,
  onEditShapePoints,
  onEditHolePoints,
  drawTool: controlledDrawTool,
  onDrawToolChange,
  toolBarContainer,
  extractContainer,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const zoom = ZOOM_STEPS[zoomIndex] ?? 100;
  const [showGrid, setShowGrid] = useState(true);
  const [spacing, setSpacing] = useState(10);
  const [extracting, setExtracting] = useState(false);
  // 점 편집 대상 — 인쇄 영역의 바깥 고리(holeIndex null) 또는 그 영역의 구멍 하나. 선택이 바뀌거나 도구·추출·그리기를 켜면
  // 끈다(pointEditOn 이 거짓이 되면 아래 효과가 지운다).
  const [pointEditTarget, setPointEditTarget] = useState<{
    readonly componentId: string;
    readonly holeIndex: number | null;
  } | null>(null);
  const pointEditId = pointEditTarget?.componentId ?? null;
  const pointEditHoleIndex = pointEditTarget?.holeIndex ?? null;
  // 구멍 점 편집을 켠 때(ms) — 더블클릭의 두 번째 누르기가 막 켠 점 편집을 바로 끄지 않게 한다.
  const holePointEditEnteredAt = useRef(Number.NEGATIVE_INFINITY);
  // 도구 상태는 작업 화면이 쥐고(사이드바 도구 줄과 함께 쓴다), 넘기지 않으면 캔버스가 쥔다.
  const [ownDrawTool, setOwnDrawTool] = useState<DrawTool>('select');
  const drawTool = controlledDrawTool ?? ownDrawTool;
  const setDrawTool = onDrawToolChange ?? setOwnDrawTool;
  const photoSize = photo?.photoSize ?? { widthPx: 0, heightPx: 0 };
  const minHandlePx = MIN_HANDLE_SCREEN_PX * usePhotoPxPerScreenPx(svgRef, photoSize.widthPx);
  const unavailable =
    disabled ||
    photo === undefined ||
    loadedUrl !== photo.photoUrl ||
    failedUrl === photo.photoUrl ||
    photoSize.widthPx <= 0 ||
    photoSize.heightPx <= 0;
  const photoShown =
    photo !== undefined && loadedUrl === photo.photoUrl && failedUrl !== photo.photoUrl;
  // 인쇄 영역은 면의 유일한 flat·cylinder 표면 위에 그린다. 그릴 수 없으면 캔버스는 평소대로다(대상은 작업 화면이 쥔다).
  const onlySurface = faceDefinition.surfaces.length === 1 ? faceDefinition.surfaces[0] : undefined;
  const drawingSurface =
    onlySurface?.type === 'flat' || onlySurface?.type === 'cylinder' ? onlySurface : undefined;
  // 인쇄 제외 영역은 사이드바 도구 줄에서 고른 도구로 그린다 — 사각형·원형은 끌어 그리고 자유 다각형은 점을 찍는다.
  const holeDrawTool = shapeDrawing?.mode === 'hole' ? (shapeDrawing.holeTool ?? 'polygon') : null;
  const holeDragTool = holeDrawTool === 'rect' || holeDrawTool === 'ellipse' ? holeDrawTool : null;
  const drawingNow =
    shapeDrawing !== null && holeDragTool === null && !unavailable && drawingSurface !== undefined;
  // 사각형·원형은 평면 표면 하나 위에서만 그린다(원통은 2단계). 자유 다각형을 그리는 중이면 쉰다.
  const rectSurface = onlySurface?.type === 'flat' ? onlySurface : undefined;
  const holeRectKind =
    holeDragTool !== null && onDrawHole !== undefined && !unavailable && rectSurface !== undefined
      ? holeDragTool
      : null;
  const canDrawRect = onDrawArea !== undefined && !unavailable && rectSurface !== undefined;
  const rectKind = drawTool !== 'select' && canDrawRect && shapeDrawing === null ? drawTool : null;
  // 원통은 끌어 그리지 않고 누르면 표면 가운데에 기본 크기로 만든다(작업 화면이 만든 영역을 고른다).
  const canCreateDefault =
    onCreateDefaultArea !== undefined && !unavailable && onlySurface?.type === 'cylinder';
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [nudgeError, setNudgeError] = useState<string | null>(null);
  const drawingBlockedReason =
    shapeDrawing === null || inputsPending
      ? null
      : shapeBlockedReason(disabled, !unavailable, drawingSurface !== undefined);
  const drawingBlockedNotice =
    drawingBlockedReason === null ? null : (
      <ShapeDrawingBlockedNotice reason={drawingBlockedReason} onCancel={onShapeCancel} />
    );
  const blockedReason = extractBlockedReason(measurement, faceDefinition.surfaces);
  const canExtract = !unavailable && blockedReason === null && !drawingNow && holeRectKind === null;
  const extractingNow = extracting && canExtract;
  useEffect(() => {
    if (!canExtract) setExtracting(false);
  }, [canExtract]);
  useEffect(() => {
    if (!canDrawRect) setDrawTool('select');
  }, [canDrawRect, setDrawTool]);
  // 사이드바에서 그리기(자유 다각형·인쇄 제외 영역)를 시작하면 인쇄 영역 도구는 선택으로 돌아간다.
  const drawingStarted = shapeDrawing !== null;
  useEffect(() => {
    if (drawingStarted) setDrawTool('select');
  }, [drawingStarted, setDrawTool]);
  const pointerBusy = extractingNow || drawingNow || rectKind !== null || holeRectKind !== null;
  const outlinePointer = useOutlinePointer({
    svgRef,
    photoSize,
    active: extractingNow,
    onClose: (points) => {
      onSurfaceExtract(points);
      setExtracting(false);
    },
    onCancel: () => setExtracting(false),
  });
  const shapePointer = useShapePointer({
    svgRef,
    photoSize,
    surface: drawingSurface,
    active: drawingNow,
    onClose: onShapeClose,
    onCancel: onShapeCancel,
    kind: shapeDrawing?.mode === 'hole' ? 'hole' : 'area',
  });
  const rectPointer = useRectDrawPointer({
    svgRef,
    photoSize,
    surface: rectSurface,
    kind: rectKind,
    onCreate: (kind, rect) => {
      const rejection = onDrawArea?.(kind, rect) ?? null;
      if (rejection === null) setDrawTool('select');
      return rejection;
    },
    onCancel: () => setDrawTool('select'),
  });
  // 인쇄 제외 영역 끌어 그리기. [+] 한 번에 구멍 하나 — 더하면 그리기와 도구 줄이 끝나고 새 구멍을 고른다(Esc 는 그만둔다).
  const holeRectPointer = useRectDrawPointer({
    svgRef,
    photoSize,
    surface: rectSurface,
    kind: holeRectKind,
    onCreate: (kind, rect) => onDrawHole?.(kind, rect) ?? null,
    onCancel: onShapeCancel,
  });
  const chooseTool = (tool: DrawTool): void => {
    setPointEditTarget(null);
    if (shapeDrawing !== null) onShapeCancel();
    setExtracting(false);
    setCreateError(null);
    if (tool !== 'select' && canCreateDefault) {
      setDrawTool('select');
      setCreateError(onCreateDefaultArea?.(tool) ?? null);
      return;
    }
    setDrawTool(tool);
  };
  // 인쇄 영역 도구 줄의 자유 다각형은 바깥(인쇄 영역)을 그릴 때만 눌린다. 구멍은 사이드바 도구 줄이 나타낸다.
  const polygonPressed = shapeDrawing?.mode === 'outer';
  const polygonBlocked =
    onBeginPolygon === undefined || unavailable || drawingSurface === undefined;
  const toolReasons = drawToolReasons({
    surfaceCount: faceDefinition.surfaces.length,
    disabled,
    photoReady: !unavailable,
  });
  // 도구를 쓸 수 있는 화면(작업 화면이 명령을 넘긴 경우)에서만 사유 줄을 보인다. 사각형 사유가 다각형 사유를 포함한다.
  const toolsWired =
    onDrawArea !== undefined || onCreateDefaultArea !== undefined || onBeginPolygon !== undefined;
  const toolNotes = toolsWired && toolReasons.rect.note !== null ? [toolReasons.rect.note] : [];
  const placementPointer = usePlacementPointer({
    svgRef,
    photoSize,
    disabled: unavailable || pointerBusy,
    onMove: onPlacementMove,
  });
  const resizePointer = useResizePointer({
    svgRef,
    photoSize,
    disabled: unavailable || pointerBusy,
    onResize: onPlacementResize ?? NO_RESIZE,
  });
  const holePointer = useHoleMovePointer({
    svgRef,
    photoSize,
    disabled: unavailable || pointerBusy,
    onMove: (componentId, index, dxMm, dyMm) =>
      onMoveHole?.(componentId, index, dxMm, dyMm) ?? null,
  });
  const [holeError, setHoleError] = useState<string | null>(null);
  // 점 편집 대상 — 선택 도구로 고른 평면 위 영역이고 조작할 수 있을 때만 켜진다(원통은 점 편집을 하지 않는다).
  const pointEditPlacement =
    pointEditId === null
      ? undefined
      : faceDefinition.placements.find((placement) => placement.componentId === pointEditId);
  const pointEditComponent = components.find((component) => component.id === pointEditId);
  const pointEditCandidate = faceDefinition.surfaces.find(
    (surface) => surface.id === pointEditPlacement?.surfaceId,
  );
  const pointEditSurface =
    pointEditCandidate?.type === 'flat' &&
    pointEditId === selectedComponentId &&
    (pointEditHoleIndex === null
      ? onEditShapePoints !== undefined
      : onEditHolePoints !== undefined && selectedHoleIndex === pointEditHoleIndex) &&
    drawTool === 'select' &&
    !pointerBusy &&
    !unavailable
      ? pointEditCandidate
      : undefined;
  // 고칠 점(컴포넌트 mm) — 바깥 고리는 점 편집 변환(사각형 네 모서리·원형 부드러운 점 넷), 구멍은 그 구멍의 점 그대로다.
  const pointEditLocal =
    pointEditComponent === undefined
      ? undefined
      : pointEditHoleIndex === null
        ? editableVerticesOf(pointEditComponent)
        : pointEditComponent.shape?.type === 'polygon'
          ? pointEditComponent.shape.holes?.[pointEditHoleIndex]
          : undefined;
  const pointEditBase =
    pointEditSurface === undefined ||
    pointEditLocal === undefined ||
    pointEditPlacement === undefined
      ? null
      : translateVertices(
          pointEditLocal,
          pointEditPlacement.position.xMm,
          pointEditPlacement.position.yMm,
        );
  const pointEditOn = pointEditBase !== null;
  useEffect(() => {
    if (pointEditId !== null && !pointEditOn) setPointEditTarget(null);
  }, [pointEditId, pointEditOn]);
  const pointEdit = usePointEditPointer({
    svgRef,
    photoSize,
    surface: pointEditOn ? pointEditSurface : undefined,
    points: pointEditBase,
    onCommit: (points) => {
      if (pointEditId === null) return null;
      if (pointEditHoleIndex === null) return onEditShapePoints?.(pointEditId, points) ?? null;
      return onEditHolePoints?.(pointEditId, pointEditHoleIndex, points) ?? null;
    },
    targetKey: `${pointEditId ?? ''}:${pointEditHoleIndex ?? 'outer'}`,
    minPointsMessage:
      pointEditHoleIndex === null ? undefined : '인쇄 제외 영역 점은 최소 3개가 있어야 합니다.',
  });
  /** 구멍 점을 끄는 동안 그 구멍을 미리보기 점(컴포넌트 mm)으로 바꾼 영역(미리보기 전용). */
  const holeEditedOf = (component: AreaComponent, position: PlacementPosition): AreaComponent => {
    const shape = component.shape;
    if (
      !pointEdit.dragging ||
      pointEditHoleIndex === null ||
      component.id !== pointEditId ||
      shape?.type !== 'polygon' ||
      pointEdit.points === null
    )
      return component;
    const local = translateVertices(pointEdit.points, -position.xMm, -position.yMm);
    return {
      ...component,
      shape: {
        ...shape,
        holes: (shape.holes ?? []).map((hole, index) =>
          index === pointEditHoleIndex ? local : hole,
        ),
      },
    };
  };
  /** 고른 평면 위 영역의 점 편집을 켠다. 원통·점 편집을 쓰지 않는 화면이면 아무것도 하지 않는다. */
  const enterPointEdit = (componentId: string): void => {
    if (onEditShapePoints === undefined || pointerBusy || drawTool !== 'select') return;
    const placement = faceDefinition.placements.find((item) => item.componentId === componentId);
    const surface = faceDefinition.surfaces.find((item) => item.id === placement?.surfaceId);
    if (surface?.type !== 'flat') return;
    placementPointer.cancel();
    resizePointer.cancel();
    holePointer.cancel();
    setPointEditTarget({ componentId, holeIndex: null });
  };
  /** 고른 구멍의 점 편집을 켠다(평면 위 영역만). */
  const enterHolePointEdit = (componentId: string, index: number): void => {
    if (onEditHolePoints === undefined || pointerBusy || drawTool !== 'select') return;
    const placement = faceDefinition.placements.find((item) => item.componentId === componentId);
    const surface = faceDefinition.surfaces.find((item) => item.id === placement?.surfaceId);
    if (surface?.type !== 'flat') return;
    placementPointer.cancel();
    resizePointer.cancel();
    holePointer.cancel();
    holePointEditEnteredAt.current = performance.now();
    setPointEditTarget({ componentId, holeIndex: index });
  };
  /** 선택 도구로 고른 영역(빈 곳은 null). 이미 그 선택이거나 선택을 쓰지 않는 화면이면 그대로 진행한다. */
  const selectComponent = (componentId: string | null): boolean =>
    onSelectComponent === undefined || componentId === selectedComponentId
      ? true
      : onSelectComponent(componentId);
  // 선택이 바뀌면(Esc·빈 곳·다른 영역) 지난 크기 바꾸기·지우기·옮기기 거부 사유는 더 이상 해당하지 않는다.
  const dismissResizeError = resizePointer.dismiss;
  const dismissHoleMoveError = holePointer.dismiss;
  // biome-ignore lint/correctness/useExhaustiveDependencies: 고른 영역이 바뀔 때마다 사유를 거둔다.
  useEffect(() => {
    dismissResizeError();
    setDeleteError(null);
    setNudgeError(null);
  }, [selectedComponentId, dismissResizeError]);
  // 구멍 선택이 바뀌면 지난 구멍 옮기기·지우기 거부 사유도 거둔다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 고른 구멍이 바뀔 때마다 사유를 거둔다.
  useEffect(() => {
    dismissHoleMoveError();
    setHoleError(null);
  }, [selectedHoleIndex, dismissHoleMoveError]);
  const selectionActive =
    onSelectComponent !== undefined && selectedComponentId !== null && !pointerBusy;
  // 고른 영역이 이 면에 놓여 있고 조작할 수 있을 때만 지우거나(Delete 키) 화살표 키로 옮긴다.
  const actionableId =
    selectionActive &&
    !unavailable &&
    onDeletePlacement !== undefined &&
    faceDefinition.placements.some((placement) => placement.componentId === selectedComponentId)
      ? selectedComponentId
      : null;
  // 고른 구멍 — 고른 영역이 이 면의 평면 위에 놓인 자유 다각형이고 그 순번의 구멍이 있을 때만 키로 다룬다.
  const actionableComponent = components.find((item) => item.id === actionableId);
  const actionableHole =
    actionableComponent !== undefined &&
    selectedHoleIndex !== null &&
    !pointEditOn &&
    actionableComponent.shape?.type === 'polygon' &&
    actionableComponent.shape.holes?.[selectedHoleIndex] !== undefined
      ? { componentId: actionableComponent.id, index: selectedHoleIndex }
      : null;
  /** 고른 구멍을 dx·dy mm 옮긴다(화살표 키). 대상이 있어 키를 처리했으면 true. */
  const nudgeHole = (dxMm: number, dyMm: number): boolean => {
    if (actionableHole === null || onMoveHole === undefined) return false;
    setHoleError(onMoveHole(actionableHole.componentId, actionableHole.index, dxMm, dyMm));
    return true;
  };
  /** 고른 구멍을 지운다(Delete 키). 대상이 있어 키를 처리했으면 true. */
  const deleteHole = (): boolean => {
    if (actionableHole === null || onDeleteHole === undefined) return false;
    holePointer.cancel();
    setHoleError(onDeleteHole(actionableHole.componentId, actionableHole.index));
    return true;
  };
  const holeKeysRef = useRef({
    on: actionableHole !== null,
    nudge: nudgeHole,
    remove: deleteHole,
    deselect: () => onSelectHole?.(null),
  });
  holeKeysRef.current = {
    on: actionableHole !== null,
    nudge: nudgeHole,
    remove: deleteHole,
    deselect: () => onSelectHole?.(null),
  };
  /** 고른 영역을 지운다. 지울 대상이 있어 키를 처리했으면 true(거부돼도 true — 사유를 보인다). */
  const deleteSelected = (): boolean => {
    if (actionableId === null || onDeletePlacement === undefined) return false;
    placementPointer.cancel();
    resizePointer.cancel();
    setDeleteError(onDeletePlacement(actionableId));
    return true;
  };
  /**
   * 화살표 키 — 고른 영역을 dx·dy mm 옮긴다. 끌어 옮기기와 같이 표면 규칙 안으로 자르고, 거부되면 사유를 보인다.
   * 옮길 대상이 있어 키를 처리했으면 true(표면 끝이라 그대로여도 true — 화면이 대신 스크롤되지 않게 한다).
   */
  const nudgeSelected = (dxMm: number, dyMm: number): boolean => {
    if (actionableId === null || onPlacementNudge === undefined) return false;
    const placement = faceDefinition.placements.find((item) => item.componentId === actionableId);
    const component = components.find((item) => item.id === actionableId);
    const surface = faceDefinition.surfaces.find((item) => item.id === placement?.surfaceId);
    if (placement === undefined || component === undefined || surface === undefined) return false;
    const position = clampToSurface(component, surface, {
      xMm: placement.position.xMm + dxMm,
      yMm: placement.position.yMm + dyMm,
    });
    if (
      position === null ||
      (position.xMm === placement.position.xMm && position.yMm === placement.position.yMm)
    )
      return true;
    setNudgeError(onPlacementNudge(actionableId, position));
    return true;
  };
  const nudgeSelectedRef = useRef(nudgeSelected);
  nudgeSelectedRef.current = nudgeSelected;
  // 키 처리기는 선택이 바뀔 때만 다시 달리므로, 지우기는 늘 지금 렌더의 값을 쓰게 ref 로 넘긴다.
  const deleteSelectedRef = useRef(deleteSelected);
  deleteSelectedRef.current = deleteSelected;
  // 점 편집 중에는 Esc 가 점 편집만 끝내고, Delete·Backspace 는 고른 점을 지우며, 화살표는 쓰지 않는다.
  const pointEditKeysRef = useRef({ on: pointEditOn, remove: pointEdit.deleteSelected });
  pointEditKeysRef.current = { on: pointEditOn, remove: pointEdit.deleteSelected };
  useEffect(() => {
    if (!selectionActive || onSelectComponent === undefined) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      if (target instanceof HTMLElement && TYPING_TAGS.includes(target.tagName)) return;
      if (pointEditKeysRef.current.on) {
        if (event.key === 'Escape') setPointEditTarget(null);
        else if (event.key === 'Delete' || event.key === 'Backspace') {
          if (pointEditKeysRef.current.remove()) event.preventDefault();
        }
        return;
      }
      // 구멍을 골랐으면 화살표·Delete·Esc 는 그 구멍에 쓴다(Esc 는 구멍 선택만 풀고 영역은 그대로 둔다).
      if (holeKeysRef.current.on) {
        const holeArrow = ARROW_STEPS[event.key];
        if (holeArrow !== undefined) {
          const step = event.shiftKey ? 10 : 1;
          if (holeKeysRef.current.nudge(holeArrow.x * step, holeArrow.y * step))
            event.preventDefault();
        } else if (event.key === 'Delete' || event.key === 'Backspace') {
          if (holeKeysRef.current.remove()) event.preventDefault();
        } else if (event.key === 'Escape') {
          holePointer.cancel();
          holeKeysRef.current.deselect();
        }
        return;
      }
      const arrow = ARROW_STEPS[event.key];
      // 실제로 처리한 키만 기본 동작(스크롤·뒤로 가기 등)을 막는다.
      if (arrow !== undefined) {
        const step = event.shiftKey ? 10 : 1;
        if (nudgeSelectedRef.current(arrow.x * step, arrow.y * step)) event.preventDefault();
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (deleteSelectedRef.current()) event.preventDefault();
        return;
      }
      if (event.key !== 'Escape') return;
      placementPointer.cancel();
      resizePointer.cancel();
      onSelectComponent(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    selectionActive,
    onSelectComponent,
    placementPointer.cancel,
    resizePointer.cancel,
    holePointer.cancel,
  ]);
  // 도구 줄은 작업 화면이 넘긴 사이드바 칸에 그린다(인쇄 영역 조작을 한곳에 모은다). 칸을 넘기지 않으면 사진 위에 둔다.
  const toolBar = (
    <DrawToolBar
      pressedTool={polygonPressed ? 'polygon' : shapeDrawing !== null ? null : drawTool}
      rectDisabled={!canDrawRect && !canCreateDefault}
      polygonDisabled={polygonBlocked}
      fullAreaDisabled={fullArea?.disabled ?? true}
      rectTitle={
        canCreateDefault
          ? CYLINDER_CREATE_TITLE
          : canDrawRect
            ? undefined
            : (toolReasons.rect.title ?? undefined)
      }
      polygonTitle={polygonBlocked ? (toolReasons.polygon.title ?? undefined) : undefined}
      fullAreaTitle={fullArea?.reason ?? undefined}
      notes={toolNotes}
      fullAreaError={fullArea?.error ?? null}
      onTool={chooseTool}
      onPolygon={() => {
        if (polygonPressed) {
          onShapeCancel();
          return;
        }
        setExtracting(false);
        setDrawTool('select');
        onBeginPolygon?.();
      }}
      onFullArea={() => fullArea?.apply()}
    />
  );
  const toolBarPortal =
    toolBarContainer === undefined || toolBarContainer === null
      ? null
      : createPortal(toolBar, toolBarContainer);
  const toolBarInline = toolBarContainer === undefined ? toolBar : null;
  if (photo === undefined)
    return (
      <div className="space-y-3">
        {toolBarPortal}
        {toolBarInline}
        {drawingBlockedNotice}
        <p className="rounded-lg border p-8 text-sm text-muted-foreground">
          현재 색상과 면의 사진을 준비해 주세요.
        </p>
      </div>
    );
  // '상품 추출하기' 버튼·막힘 사유·추출 중 안내. 작업 화면이 넘긴 사이드바 표면 설정 칸에 그리고, 칸을 넘기지 않으면 사진 위에 둔다.
  const extractControls = (
    <div className="space-y-2 text-sm">
      <ProductExtractButton
        hasSurface={faceDefinition.surfaces.length > 0}
        pressed={extractingNow}
        disabled={!canExtract}
        onToggle={() => {
          setDrawTool('select');
          setExtracting(!extracting);
        }}
      />
      {blockedReason !== null && <p className="text-muted-foreground">{blockedReason}</p>}
      {extractingNow && <KeyHints hints={POINT_KEYS} />}
    </div>
  );
  const extractPortal =
    extractContainer === undefined || extractContainer === null
      ? null
      : createPortal(extractControls, extractContainer);
  const extractInline = extractContainer === undefined ? extractControls : null;
  // 그리는 표면은 flat 과 cylinder 다. mesh 는 조회·버전 탐색만이라(ADR-0007 D10) 그리지 않는다.
  const surfaces = faceDefinition.surfaces.flatMap((surface): EditableSurface[] =>
    surface.type === 'flat' || surface.type === 'cylinder' ? [surface] : [],
  );
  // 추출 중 거부 사유와 점·모서리·몸통 드래그 거부 사유. 서로 다른 조작이라 줄을 나눠 보여준다.
  const pointerErrors = [
    createError,
    deleteError,
    nudgeError,
    outlinePointer.error,
    shapePointer.error,
    rectPointer.error,
    holeRectPointer.error,
    holePointer.error,
    holeError,
    resizePointer.error,
    pointEdit.error,
  ].filter((reason): reason is string => reason !== null);
  const selected = surfaces.find((surface) => surface.id === selectedSurfaceId);
  const hatchPx = Math.max(6, Math.max(photoSize.widthPx, photoSize.heightPx) * 0.008);
  /**
   * 평면 위 영역의 구멍 고리(사진 px). 구멍을 끄는 중이면 그 미리보기만큼 옮긴 모양을 쓴다. 원통은 null(구멍은 영역 경로가 비워 보인다).
   */
  const holeRingsOf = (
    component: AreaComponent,
    surface: EditableSurface,
    position: PlacementPosition,
  ): { x: number; y: number }[][] | null => {
    if (surface.type !== 'flat' || component.shape?.type !== 'polygon') return null;
    return (component.shape.holes ?? []).map((hole) =>
      flattenPolygonRing(hole).map((point) =>
        surfaceMmToPhoto(surface, photoSize, {
          xMm: position.xMm + point.xMm,
          yMm: position.yMm + point.yMm,
        }),
      ),
    );
  };
  /** 구멍을 끄는 동안 옮긴(또는 점을 고친) 구멍으로 바꾼 영역(미리보기 전용). */
  const holeMovedOf = (component: AreaComponent, position: PlacementPosition): AreaComponent => {
    const preview = holePointer.preview;
    const shape = component.shape;
    if (preview === null || preview.componentId !== component.id || shape?.type !== 'polygon')
      return holeEditedOf(component, position);
    return {
      ...component,
      shape: {
        ...shape,
        holes: (shape.holes ?? []).map((hole, index) =>
          index === preview.index ? translateHoleRing(hole, preview.dxMm, preview.dyMm) : hole,
        ),
      },
    };
  };
  const scale = selected === undefined ? null : surfaceScale(selected, photoSize);
  // 원통 축척은 가운데 열 값이다 — 가로 축척이 실루엣 끝으로 갈수록 줄어 한 값이 없다.
  const isCylinderScale = selected?.type === 'cylinder';
  const scaleBasis = isCylinderScale ? '가운데 열 기준 ' : '';
  const scaleNote = isCylinderScale ? ' — 가장자리로 갈수록 가로 축척이 줄어듭니다.' : '';
  const effectiveGrid = Math.max(
    spacing,
    ...components.map((component) => componentGrid(component, spacing).spacingMm),
  );
  return (
    <section className="space-y-3" aria-label="사진 편집 작업 화면">
      {toolBarPortal}
      {extractPortal}
      {toolBarInline}
      {extractInline}
      {rectKind !== null && (
        <div className="space-y-1">
          <p className="text-sm">{RECT_DRAW_GUIDE}</p>
          <KeyHints hints={RECT_KEYS} />
        </div>
      )}
      {holeRectKind !== null && shapeDrawing !== null && (
        <div className="space-y-1">
          <p className="text-sm">
            {`인쇄 영역 ‘${shapeDrawing.name}’ 안을 끌어서 인쇄 제외 영역을 그리세요.`}
          </p>
          <KeyHints hints={RECT_KEYS} />
        </div>
      )}
      {actionableHole !== null && !pointerBusy && <KeyHints hints={HOLE_KEYS} />}
      {drawingNow && (
        <div className="space-y-1">
          <p className="text-sm">
            {shapeDrawing.mode === 'hole'
              ? `인쇄 영역 ‘${shapeDrawing.name}’ 안에 인쇄 제외 영역의 경계를 따라 점을 찍고 첫 점을 눌러 닫으세요.`
              : `인쇄 영역 ‘${shapeDrawing.name}’의 경계를 따라 점을 찍고 첫 점을 눌러 닫으세요.`}
          </p>
          <KeyHints hints={POINT_KEYS} />
        </div>
      )}
      {pointEditOn && (
        <div className="space-y-1">
          <p className="text-sm">{POINT_EDIT_GUIDE}</p>
          <KeyHints hints={POINT_EDIT_KEYS} />
        </div>
      )}
      {drawingBlockedNotice}
      {pointerErrors.map((reason) => (
        <p key={reason} role="alert" className="text-sm text-destructive">
          {reason}
        </p>
      ))}
      {failedUrl === photo.photoUrl && (
        <p role="alert" className="text-sm text-destructive">
          사진을 표시할 수 없습니다. 사진 URL을 다시 조회한 뒤 조작해 주세요.
        </p>
      )}
      <div className="relative">
        <ZoomControl
          zoom={zoom}
          canZoomOut={zoomIndex > 0}
          canZoomIn={zoomIndex < ZOOM_STEPS.length - 1}
          onZoomOut={() => setZoomIndex(zoomIndex - 1)}
          onZoomIn={() => setZoomIndex(zoomIndex + 1)}
          onReset={() => setZoomIndex(DEFAULT_ZOOM_INDEX)}
        />
        <div className="max-h-[calc(100vh-2rem)] overflow-auto rounded-lg border bg-muted/30">
          <svg
            className="mx-auto block"
            ref={svgRef}
            aria-label="상품 사진과 인쇄 영역"
            viewBox={`0 0 ${photoSize.widthPx} ${photoSize.heightPx}`}
            data-zoom={zoom}
            style={{
              ...photoSizeStyle(zoom, photoSize),
              touchAction: 'none',
              cursor: pointerBusy ? 'crosshair' : 'default',
            }}
            onPointerDown={(event) => {
              if (extractingNow) outlinePointer.addPoint(event);
              else if (drawingNow) shapePointer.addPoint(event);
              else if (rectKind !== null) rectPointer.start(event);
              else if (holeRectKind !== null) holeRectPointer.start(event);
              else if (event.button === 0) {
                setPointEditTarget(null);
                selectComponent(null);
              }
            }}
            onPointerMove={(event) => {
              pointEdit.move(event);
              rectPointer.move(event);
              holeRectPointer.move(event);
              holePointer.move(event);
              resizePointer.move(event);
              placementPointer.move(event);
            }}
            onPointerUp={(event) => {
              pointEdit.finish(event);
              rectPointer.finish(event);
              holeRectPointer.finish(event);
              holePointer.finish(event);
              resizePointer.finish(event);
              placementPointer.finish(event);
            }}
            onPointerCancel={() => {
              pointEdit.cancel();
              rectPointer.cancel();
              holeRectPointer.cancel();
              holePointer.cancel();
              resizePointer.cancel();
              placementPointer.cancel();
            }}
            onLostPointerCapture={() => {
              pointEdit.cancel();
              rectPointer.cancel();
              holeRectPointer.cancel();
              holePointer.cancel();
              resizePointer.cancel();
              placementPointer.cancel();
            }}
          >
            <title>사진 위 표면과 인쇄 영역</title>
            {/* 미리보기 multiply 가 사진하고만 섞이도록 사진과 미리보기를 한 묶음으로 격리한다. */}
            <g style={{ isolation: 'isolate' }}>
              <image
                data-testid="edit-area-photo"
                href={photo.photoUrl}
                width={photoSize.widthPx}
                height={photoSize.heightPx}
                onLoad={() => {
                  setLoadedUrl(photo.photoUrl);
                  setFailedUrl(null);
                }}
                onError={() => setFailedUrl(photo.photoUrl)}
              />
              {cylinderPreview !== null && photoShown ? (
                <CylinderPreviewLayer
                  image={cylinderPreview.image}
                  multiply={cylinderPreview.multiply}
                  maskUrl={cylinderPreview.maskUrl}
                  photoSize={photoSize}
                />
              ) : null}
            </g>
            {/* 다시 추출하는 동안에는 옛 틀·윤곽·인쇄 영역을 숨겨 깨끗한 사진 위에 찍게 한다. 데이터는 그대로이고 새 윤곽을 닫을 때 바뀐다(Esc 면 그대로 돌아온다). */}
            {extractingNow ? null : (
              <>
                {/* 표면 틀·윤곽은 안내선일 뿐이다 — 포인터를 받지 않아 아래 인쇄 영역·구멍·빈 사진으로 누르기가 지나간다. */}
                {surfaces.map((surface) =>
                  surface.type === 'flat' ? (
                    <FlatSurfaceOverlay key={surface.id} surface={surface} photoSize={photoSize} />
                  ) : (
                    <CylinderSurfaceOverlay
                      key={surface.id}
                      surface={surface}
                      photoSize={photoSize}
                    />
                  ),
                )}
                {surfaces.map((surface) => (
                  <SurfaceOutlineOverlay
                    key={`outline-${surface.id}`}
                    surface={surface}
                    photoSize={photoSize}
                  />
                ))}
                {faceDefinition.placements.map((placement) => {
                  const surface = surfaces.find((item) => item.id === placement.surfaceId);
                  const component = components.find((item) => item.id === placement.componentId);
                  if (surface === undefined || component === undefined) return null;
                  const resizing =
                    resizePointer.preview?.componentId === component.id
                      ? resizePointer.preview.rect
                      : null;
                  // 점을 끄는 동안에는 미리보기 점열로 바꾼 모양·위치로 그린다(놓을 때 스토어가 같은 정규화를 한다).
                  const pointDragged =
                    pointEdit.dragging &&
                    component.id === pointEditId &&
                    pointEditHoleIndex === null &&
                    pointEdit.points !== null
                      ? normalizeEditedShape(
                          pointEdit.points,
                          component.shape?.type === 'polygon' ? component.shape.holes : undefined,
                          placement.position,
                        )
                      : null;
                  const holeShown = holeMovedOf(component, placement.position);
                  const shown =
                    pointDragged !== null
                      ? { ...placement, position: pointDragged.position }
                      : resizing !== null
                        ? { ...placement, position: { xMm: resizing.xMm, yMm: resizing.yMm } }
                        : placementPointer.preview?.componentId === component.id
                          ? { ...placement, position: placementPointer.preview.position }
                          : placement;
                  return (
                    <PlacementOverlay
                      key={component.id}
                      component={
                        pointDragged !== null
                          ? {
                              ...component,
                              widthMm: pointDragged.widthMm,
                              heightMm: pointDragged.heightMm,
                              shape: pointDragged.shape,
                            }
                          : resizing === null
                            ? holeShown
                            : resizedComponentOf(component, resizing)
                      }
                      surface={surface}
                      placement={shown}
                      photoSize={photoSize}
                      disabled={unavailable || pointerBusy}
                      gridSpacing={showGrid ? effectiveGrid : null}
                      selected={component.id === selectedComponentId}
                      onPointerStart={(event) => {
                        if (pointerBusy || event.button !== 0) return;
                        event.stopPropagation();
                        // 점 편집 중인 영역의 몸통은 옮기지 않는다(점·손잡이·선으로만 고친다).
                        if (
                          pointEditOn &&
                          component.id === pointEditId &&
                          pointEditHoleIndex === null
                        )
                          return;
                        // 구멍 점 편집 중에 영역 몸통을 누르면 점 편집을 끝내고 아래 규칙대로 구멍 선택을 푼다.
                        if (pointEditHoleIndex !== null) setPointEditTarget(null);
                        // 구멍을 고른 채 영역 몸통을 누르면 구멍 선택만 풀고 영역을 옮긴다(점 편집은 켜지 않는다).
                        const holeWasSelected =
                          component.id === selectedComponentId && selectedHoleIndex !== null;
                        if (holeWasSelected) onSelectHole?.(null);
                        const wasSelected =
                          component.id === selectedComponentId && !holeWasSelected;
                        if (!selectComponent(component.id)) return;
                        // 이미 고른 영역을 옮기지 않고 다시 누르면 점 편집을 켠다.
                        placementPointer.start(
                          event,
                          component,
                          surface,
                          placement,
                          wasSelected ? () => enterPointEdit(component.id) : undefined,
                        );
                      }}
                    />
                  );
                })}
                {faceDefinition.placements.map((placement) => {
                  const surface = surfaces.find((item) => item.id === placement.surfaceId);
                  const component = components.find((item) => item.id === placement.componentId);
                  if (surface?.type !== 'flat' || component === undefined) return null;
                  // 크기를 바꾸거나 점을 끄는 동안에는 영역 경로가 구멍을 함께 보인다(빗금은 잠시 숨긴다).
                  if (resizePointer.preview?.componentId === component.id) return null;
                  if (
                    pointEdit.dragging &&
                    component.id === pointEditId &&
                    pointEditHoleIndex === null
                  )
                    return null;
                  const position =
                    placementPointer.preview?.componentId === component.id
                      ? placementPointer.preview.position
                      : placement.position;
                  const rings = holeRingsOf(holeMovedOf(component, position), surface, position);
                  if (rings === null) return null;
                  const parentSelected = component.id === selectedComponentId;
                  const interactive =
                    parentSelected &&
                    onSelectHole !== undefined &&
                    drawTool === 'select' &&
                    !pointerBusy &&
                    !unavailable &&
                    !(pointEditOn && component.id === pointEditId && pointEditHoleIndex === null);
                  return rings.map((ring, index) => (
                    <ShapeHoleOverlay
                      // biome-ignore lint/suspicious/noArrayIndexKey: 구멍에는 별도 ID 가 없고 순서가 식별자다.
                      key={`hole-${component.id}-${index}`}
                      ring={ring}
                      selected={parentSelected && selectedHoleIndex === index}
                      interactive={interactive}
                      label={`${component.name} 인쇄 제외 영역 ${index + 1}`}
                      hatchPx={hatchPx}
                      testId={`hole-${component.id}-${index}`}
                      onPointerDown={(event) => {
                        if (!interactive || event.button !== 0) return;
                        event.stopPropagation();
                        placementPointer.cancel();
                        resizePointer.cancel();
                        // 구멍 점 편집 중에 구멍 안을 누르면 점 편집만 끝낸다(다른 구멍이면 그 구멍을 고른다).
                        // 막 켠 구멍을 곧바로 다시 누른 것(더블클릭의 두 번째 누르기)은 점 편집을 그대로 둔다.
                        if (pointEditOn && component.id === pointEditId) {
                          if (
                            index === pointEditHoleIndex &&
                            performance.now() - holePointEditEnteredAt.current <= DOUBLE_PRESS_MS
                          )
                            return;
                          setPointEditTarget(null);
                          if (index === pointEditHoleIndex) return;
                        }
                        const wasSelected = parentSelected && selectedHoleIndex === index;
                        onSelectHole?.(index);
                        // 이미 고른 구멍을 옮기지 않고 다시 누르면 그 구멍의 점 편집을 켠다.
                        holePointer.start(
                          event,
                          component.id,
                          index,
                          surface,
                          wasSelected ? () => enterHolePointEdit(component.id, index) : undefined,
                        );
                      }}
                    />
                  ));
                })}
              </>
            )}
            {faceDefinition.placements.map((placement) => {
              // 크기 핸들은 평면 위에 놓인 선택 영역에만 보인다. 원통은 인스펙터 수치로 바꾼다.
              if (placement.componentId !== selectedComponentId || onPlacementResize === undefined)
                return null;
              if (pointerBusy || pointEditOn) return null;
              const surface = surfaces.find((item) => item.id === placement.surfaceId);
              const component = components.find((item) => item.id === placement.componentId);
              if (surface?.type !== 'flat' || component === undefined) return null;
              const resizing =
                resizePointer.preview?.componentId === component.id
                  ? resizePointer.preview.rect
                  : null;
              const position =
                placementPointer.preview?.componentId === component.id
                  ? placementPointer.preview.position
                  : placement.position;
              const rect = resizing ?? {
                ...position,
                widthMm: component.widthMm,
                heightMm: component.heightMm,
              };
              return (
                <g key={`resize-${component.id}`}>
                  <ResizeHandlesOverlay
                    rect={rect}
                    surface={surface}
                    photoSize={photoSize}
                    cornersOnly={component.shape?.type === 'polygon'}
                    disabled={unavailable}
                    minSizePx={minHandlePx}
                    onHandleStart={(event, handle) => {
                      event.stopPropagation();
                      resizePointer.start(event, handle, component.id, surface, {
                        ...placement.position,
                        widthMm: component.widthMm,
                        heightMm: component.heightMm,
                      });
                    }}
                  />
                  {resizing === null ? null : (
                    <MmSizeLabel rect={resizing} surface={surface} photoSize={photoSize} />
                  )}
                </g>
              );
            })}
            {pointEditSurface === undefined || pointEdit.points === null ? null : (
              <PointEditOverlay
                points={pointEdit.points}
                surface={pointEditSurface}
                photoSize={photoSize}
                selectedIndex={pointEdit.selectedIndex}
                minSizePx={minHandlePx}
                onVertexStart={pointEdit.startVertex}
                onHandleStart={pointEdit.startHandle}
                onRingPointerDown={pointEdit.insertAt}
              />
            )}
            <OutlinePathOverlay
              pointsPx={outlinePointer.points.map((point) => ({
                x: (point.x * photoSize.widthPx) / 100,
                y: (point.y * photoSize.heightPx) / 100,
              }))}
              photoSize={photoSize}
              canClose={outlinePointer.points.length >= MIN_OUTLINE_POINTS}
            />
            <OutlinePathOverlay
              pointsPx={shapePointer.pointsPx}
              photoSize={photoSize}
              canClose={shapePointer.points.length >= MIN_SHAPE_POINTS}
            />
            {rectSurface !== undefined && holeRectPointer.preview !== null ? (
              <DrawPreviewOverlay
                kind={holeRectPointer.preview.kind}
                rect={holeRectPointer.preview.rect}
                surface={rectSurface}
                photoSize={photoSize}
                muted={!holeRectPointer.preview.ok}
              />
            ) : null}
            {rectSurface !== undefined && rectPointer.preview !== null ? (
              <DrawPreviewOverlay
                kind={rectPointer.preview.kind}
                rect={rectPointer.preview.rect}
                surface={rectSurface}
                photoSize={photoSize}
                muted={!rectPointer.preview.ok}
              />
            ) : null}
          </svg>
        </div>
      </div>
      {/* Grid 줄은 사진 칸 아래에 둔다 — 넓은 화면에서 사진 칸 윗선이 사이드바 첫 칸 윗선과 같은 높이에서 시작한다. */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={showGrid}
            onChange={(event) => setShowGrid(event.target.checked)}
          />
          실측 Grid
        </label>
        <label>
          간격{' '}
          <select
            aria-label="Grid 간격 mm"
            value={spacing}
            onChange={(event) => setSpacing(Number(event.target.value))}
          >
            {[1, 5, 10, 20, 50].map((value) => (
              <option key={value} value={value}>
                {value} mm
              </option>
            ))}
          </select>
        </label>
      </div>
      {scale !== null && (
        <p className="text-xs text-muted-foreground">
          사진 원본 축척: {scaleBasis}가로 {scale.x.toFixed(3)} px/mm · 세로 {scale.y.toFixed(3)}{' '}
          px/mm (화면 확대 {zoom}%){scaleNote}
        </p>
      )}
      {showGrid && (
        <p className="text-xs text-muted-foreground">
          Grid 간격 {effectiveGrid} mm
          {effectiveGrid > spacing ? ' — 선 개수를 제한하기 위해 간격을 늘렸습니다.' : ''}. 인쇄
          정확성을 자동으로 보증하지 않습니다.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        제품 경계를 따라 윤곽을 잡고, 다른 색상 사진에서도 배치를 확인하세요.
      </p>
    </section>
  );
}
